/* Knobs.co — Price Request handler (Worker-backed, truthful success).
   ─────────────────────────────────────────────────────────────────
   For $0 / quote-only products. Submits the request as JSON to the same
   Cloudflare Worker the Trade application uses (form data-endpoint attr,
   type:"price-request"). The Worker creates a DRAFT ORDER tagged
   `price-request` via the Admin API — one line item resolved from
   `product_sku`, and a note that prints Name/Email/Phone/Product/SKU/URL/
   Message (see #D117) — and returns ok ONLY after Shopify confirms the
   write. `message` is the field that reaches a human, which is why the
   configuration summary rides in it.

   Success is shown ONLY on confirmed ok:true — never on faith. On any
   failure the customer sees an error with a phone fallback, so a lost
   request is never silently swallowed. On confirmed success we also fire
   the Klaviyo identify + "Price Request Submitted" event for tracking.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-prq]');
  if (!root) return;

  var form = root.querySelector('[data-prq-form]');
  var errEl = root.querySelector('[data-prq-error]');
  var successEl = root.querySelector('[data-prq-success]');
  var endpoint = form ? form.getAttribute('data-endpoint') : '';
  var eventName = root.getAttribute('data-klaviyo-event') || 'Price Request Submitted';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var FALLBACK = ' Please call 800-530-8245 and we’ll price it for you directly.';

  function klaviyoPush(tuple) {
    try { window.klaviyo = window.klaviyo || []; window.klaviyo.push(tuple); return true; }
    catch (e) { return false; }
  }
  function val(name) {
    if (!form) return '';
    var el = form.elements[name];
    return el ? (el.value || '').trim() : '';
  }
  function attr(name) { return root.getAttribute(name) || ''; }
  /* First-touch origin from the sitewide knobs_ft cookie (snippets/knobs-first-touch.liquid),
     read the same way knobs-trade-apply-v2.js does. Added 2026-10-18. */
  function readFirstTouch() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)knobs_ft=([^;]+)/);
      return m ? JSON.parse(decodeURIComponent(m[1])) : {};
    } catch (e) { return {}; }
  }
  function showError(msg) { if (errEl) { errEl.textContent = msg; errEl.hidden = false; } }
  function clearError() { if (errEl) { errEl.textContent = ''; errEl.hidden = true; } }

  /* Incomplete-selection warning. Set once the shopper chooses "Send anyway", so a
     second submit goes straight through instead of re-asking. */
  var warnEl = root.querySelector('[data-prq-warn]');
  var warnTextEl = root.querySelector('[data-prq-warn-text]');
  var acknowledgedIncomplete = false;

  function listPhrase(items) {
    if (items.length === 1) return items[0];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }
  function showWarning(missing) {
    if (!warnEl) { acknowledgedIncomplete = true; return; }  /* no UI: never trap the request */
    if (warnTextEl) {
      warnTextEl.textContent = 'You haven’t chosen ' + listPhrase(missing) +
        '. We can price it more accurately with that.';
    }
    warnEl.hidden = false;
    try { warnEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (e) {}
  }
  function hideWarning() { if (warnEl) warnEl.hidden = true; }

  /* ── Configurator capture ──────────────────────────────────────────────
     A price request used to send only name/email/phone/message plus the
     product title and the DEFAULT variant's SKU, both rendered into data-*
     attributes at page load. The shopper's finish, size and mounting choices
     live as hidden properties[...] inputs in the ADD-TO-CART form and were
     never read, so the request arrived with nothing to price — draft order
     #D117 (EMT-86345, 2026-09-07) is exactly that, reported by Kayla.

     These read the configurator's live state, so what the shopper is looking
     at travels with the request. `[data-cfg-prop]` is the same set of inputs
     add-to-cart submits, so the two paths now carry identical information. */

  function hasConfigurator() {
    return !!document.querySelector('[data-cfg-prop], .knobs-cfg__group');
  }

  /* Every selection the configurator currently holds, keyed as it is on the
     cart line ("Finish", "Configured Code", each option group's label). */
  function readConfiguration() {
    var out = {};
    document.querySelectorAll('[data-cfg-prop]').forEach(function (el) {
      if (el.disabled) return;                 /* Pricing Notice etc. are parked disabled */
      var key = el.getAttribute('data-cfg-prop');
      var v = (el.value || '').trim();
      if (key && v) out[key] = v;
    });
    return out;
  }

  /* Required option groups the shopper has not answered. The configurator
     pre-selects a default for required axes, so this is normally empty — but
     when it is not, the request cannot be priced and must not be sent. */
  function missingSelections() {
    var missing = [];
    document.querySelectorAll('.knobs-cfg__group[data-cfg-required="1"]').forEach(function (g) {
      var state = g.querySelector('[data-cfg-choice-state]');
      var label = (g.getAttribute('data-cfg-group-label') || 'option').trim();
      if (!state || !String(state.value || '').trim()) missing.push(label);
    });
    var finishKey = document.querySelector('[data-cfg-finish-key]');
    if (finishKey && !String(finishKey.value || '').trim()) missing.push('Finish');
    return missing;
  }

  /* Readable summary appended to the message, so the selections reach the
     draft order through the field the Worker already forwards.

     Anything the shopper skipped is listed as "not specified" rather than left
     out. An absent line is indistinguishable from a bug — which is the whole
     reason this was reported — whereas "Finish: not specified" tells the team
     to ask. */
  function describeConfiguration(cfg, missing) {
    var lines = [];
    Object.keys(cfg).forEach(function (k) {
      if (k.charAt(0) === '_') return;         /* internal keys are not for humans */
      if (k === 'Configured Code' || k === 'Component Codes') return;
      lines.push(k + ': ' + cfg[k]);
    });
    (missing || []).forEach(function (label) {
      if (!cfg[label]) lines.push(label + ': not specified — customer unsure');
    });
    if (cfg['Configured Code']) lines.unshift('Item code: ' + cfg['Configured Code']);
    return lines.length ? lines.join('\n') : '';
  }

  function openForm() {
    root.hidden = false;
    /* Re-evaluate against whatever is selected now: a warning left over from a
       previous attempt would otherwise still be on screen after they went and
       chose the missing option. */
    hideWarning();
    clearError();
    acknowledgedIncomplete = false;
    try { root.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
    var email = form && form.elements['email'];
    if (email) { try { email.focus({ preventScroll: true }); } catch (e) {} }
  }
  function closeForm() { root.hidden = true; }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-price-request-open]')) { e.preventDefault(); openForm(); return; }
    if (e.target.closest('[data-prq-close]')) { e.preventDefault(); closeForm(); return; }

    /* "Let me choose" — close the form and put them back on the configurator. */
    if (e.target.closest('[data-prq-warn-back]')) {
      e.preventDefault();
      hideWarning();
      closeForm();
      var cfgHost = document.querySelector('.knobs-cfg__group, [data-cfg-prop]');
      var scrollTo = cfgHost && cfgHost.closest ? (cfgHost.closest('.knobs-cfg') || cfgHost) : cfgHost;
      if (scrollTo) { try { scrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err) {} }
      return;
    }
    /* "Send anyway" — proceed with whatever is chosen; gaps go through as
       "not specified" so the team knows to ask rather than seeing a blank. */
    if (e.target.closest('[data-prq-warn-send]')) {
      e.preventDefault();
      acknowledgedIncomplete = true;
      hideWarning();
      if (form) {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  });

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearError();

      var missing = hasConfigurator() ? missingSelections() : [];
      var cfg = hasConfigurator() ? readConfiguration() : {};
      var summary = describeConfiguration(cfg, missing);

      var d = {
        type: 'price-request',
        name: val('name'),
        email: val('email'),
        phone: val('phone'),
        /* The summary rides along in `message` because that is a field the Worker
           already forwards onto the Shopify note. `configuration` carries the same
           thing structurally for when the Worker can map it onto line-item
           properties directly. */
        message: summary ? (val('message') ? val('message') + '\n\n' + summary : summary) : val('message'),
        fax: val('fax'), /* honeypot */
        variant_id: attr('data-variant-id'),
        product_title: attr('data-product-title'),
        /* `product_sku` stays the LISTING's SKU. The Worker resolves the draft-order
           line item from it (see #D117's "SKU: EMT-86345"), and a configured code
           like EMT-86345-US15 is not a Shopify SKU — putting it here would break the
           very requests this fixes. The configured code travels alongside instead,
           and leads the summary as "Item code:" so the team reads it in the note. */
        product_sku: attr('data-product-sku'),
        configured_sku: cfg['Configured Code'] || '',
        configuration: cfg,
        product_url: attr('data-product-url')
      };

      /* first-touch origin (sitewide knobs_ft cookie) + current-page fallback */
      var ft = readFirstTouch();
      var qp = new URLSearchParams(location.search);
      d.ft_source = ft.s || qp.get('utm_source') || '';
      d.ft_medium = ft.m || qp.get('utm_medium') || '';
      d.ft_campaign = ft.c || qp.get('utm_campaign') || '';
      d.ft_referrer = ft.ref || document.referrer || '';
      d.ft_landing = ft.lp || location.pathname || '';
      d.ft_ts = ft.ts || '';

      if (!EMAIL_RE.test(d.email)) {
        return showError('Please enter a valid email so we can send you pricing.');
      }

      /* Incomplete selections WARN, they do not block (Dave, 2026-09-07). Someone who
         genuinely does not know the finish still needs to be able to ask, and a hard
         requirement would simply lose that request. Say what is unanswered, offer to
         go back, and let them send it regardless. `d.incomplete` marks it so the team
         sees at a glance that options are still open. */
      if (missing.length && !acknowledgedIncomplete) {
        showWarning(missing);
        return;
      }
      d.incomplete = missing;
      if (!endpoint || !window.fetch) {
        return showError('We couldn’t open a connection.' + FALLBACK);
      }

      var btn = form.querySelector('button[type=submit]');
      var btnLabel = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      function restore() { if (btn) { btn.disabled = false; btn.textContent = btnLabel; } }

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(d)
      }).then(function (r) {
        return r.json().catch(function () { return { ok: false }; });
      }).then(function (res) {
        if (!res || res.ok !== true) {
          restore();
          return showError('Something went wrong sending your request.' + FALLBACK);
        }

        /* confirmed write — now (and only now) success + Klaviyo */
        klaviyoPush(['identify', {
          '$email': d.email,
          '$first_name': d.name,
          '$phone_number': d.phone
        }]);
        klaviyoPush(['track', eventName, {
          product_title: d.product_title,
          product_sku: d.product_sku,
          configured_sku: d.configured_sku,
          product_vendor: attr('data-product-vendor'),
          product_url: d.product_url,
          message: d.message,
          /* Flattened as well as structured: the notification template can print
             `configuration_summary` without iterating an object. */
          configuration: d.configuration,
          configuration_summary: summary,
          source: 'price-request-pdp'
        }]);

        if (successEl) {
          form.hidden = true;
          successEl.hidden = false;
          try { successEl.setAttribute('tabindex', '-1'); successEl.focus(); } catch (e) {}
          try { successEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
        }
      }).catch(function () {
        restore();
        showError('We couldn’t reach the request service.' + FALLBACK);
      });
    });
  }

  // Collection cards link here with #price-request — auto-open on arrival.
  if (window.location.hash === '#price-request') {
    if (document.readyState !== 'loading') openForm();
    else document.addEventListener('DOMContentLoaded', openForm);
  }
})();

